import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

import { TIN_IE_LENGTH } from 'const';
import { IValidator, IValidateForm } from 'interfaces/validator';
import { registration } from 'services/auth';
import YM from 'utils/metrics';
import { validateValue, validateForm } from 'utils/validation';
import Button from 'components/Button';
import Card from 'components/Card';
import Input from 'components/Input';
import InputPassword from 'components/Input/InputPassword';
import InputPhone from 'components/Input/InputPhone';
import ModalInfo from 'components/Modals/ModalInfo';
import ModalError from 'components/Modals/ModalError';

import classes from './registration.module.scss';
import { IFormsData, IFormField } from './types';

const INVALID_PASSWORD_MESSAGE =
	'Должен содержать минимум одну заглавную и одну строчную букву и не менее одного спецсимвола "-_,.!\\*"';

const Registration: React.FC = () => {
	const navigate = useNavigate();
	const [userData, setUserData] = useState({
		lastName: '',
		firstName: '',
		middleName: '',
		email: '',
		phone: '',
	});
	const [passwordData, setPasswordData] = useState({
		password: '',
		confirmPassword: '',
	});
	const [companyData, setCompanyData] = useState({
		name: '',
		tin: '',
		kpp: '',
		ogrn: '',
		country: '',
		region: '',
		address: '',
		phone: '',
		is_ie: false,
	});
	const [step, setStep] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<{ [key: string]: string }>({
		lastName: '',
		firstName: '',
		middleName: '',
		email: '',
		phone: '',
		password: '',
		confirmPassword: '',
		name: '',
		tin: '',
		kpp: '',
		ogrn: '',
		country: '',
		region: '',
		address: '',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [registrationError, setRegistrationError] = useState('');

	const validateFieldValue = ({
		type,
		value,
		isRequired,
		confirmValue,
	}: IValidator): void => {
		const error = validateValue({ type, value, isRequired, confirmValue });
		const isPassword = type === 'password';
		const isInvalidPassword = isPassword && value.length >= 8 && error;
		setErrors({
			...errors,
			[type]: isInvalidPassword ? INVALID_PASSWORD_MESSAGE : error,
		});
	};

	const handleChangeUserData =
		(type: string, isRequired: boolean) =>
		(value: string): void => {
			if (value.length === 1 && type !== 'email') {
				value = value.toUpperCase();
			}
			setUserData({ ...userData, [type]: value });
			if (type !== 'email' && type !== 'phone') {
				validateFieldValue({ type, value, isRequired });
			}
		};

	const validateUserData =
		(type: 'email' | 'phone', isRequired: boolean) => (): void => {
			const value: string = userData[type];
			validateFieldValue({ type, value, isRequired });
		};

	const handleChangePasswordData =
		(type: string, isRequired: boolean) =>
		(value: string): void => {
			setPasswordData({ ...passwordData, [type]: value });
			validateFieldValue({
				type,
				value,
				isRequired,
				confirmValue:
					type === 'confirmPassword' ? passwordData.password : '',
			});
		};

	const handleChangeCompanyData =
		(type: string, isRequired: boolean) =>
		(value: string): void => {
			const newCompanyData = { ...companyData };
			const fieldData = { type, value, isRequired, confirmValue: false };

			if (type === 'tin') {
				newCompanyData.is_ie = value.length === TIN_IE_LENGTH;
			}
			if (type === 'ogrn') {
				fieldData.confirmValue = companyData.is_ie;
			}

			setCompanyData({ ...newCompanyData, [type]: value });
			validateFieldValue(fieldData);
		};

	useEffect(() => {
		if (!companyData.kpp && !errors.kpp) return;

		validateFieldValue({
			type: 'kpp',
			value: companyData.kpp,
			isRequired: !companyData.is_ie,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyData.is_ie]);

	const formData: IFormsData = {
		1: {
			title: 'Личные данные',
			formFields: [
				{
					id: 'lastName',
					value: userData.lastName,
					onChange: handleChangeUserData('lastName', true),
					placeholder: 'Фамилия',
					isRequired: true,
				},
				{
					id: 'firstName',
					value: userData.firstName,
					onChange: handleChangeUserData('firstName', true),
					placeholder: 'Имя',
					isRequired: true,
				},
				{
					id: 'middleName',
					value: userData.middleName,
					onChange: handleChangeUserData('middleName', false),
					placeholder: 'Отчество',
					isRequired: false,
				},
				{
					id: 'email',
					value: userData.email,
					onChange: handleChangeUserData('email', true),
					onBlur: validateUserData('email', true),
					placeholder: 'Почта',
					isRequired: true,
				},
				{
					id: 'phone',
					value: userData.phone,
					onChange: handleChangeUserData('phone', false),
					onBlur: validateUserData('phone', false),
					placeholder: 'Телефон',
					isRequired: false,
					type: 'phone',
				},
			],
		},
		2: {
			title: 'Создание пароля',
			formFields: [
				{
					id: 'password',
					value: passwordData.password,
					onChange: handleChangePasswordData('password', true),
					placeholder: 'Придумайте пароль',
					isRequired: true,
					type: 'password',
				},
				{
					id: 'confirmPassword',
					value: passwordData.confirmPassword,
					onChange: handleChangePasswordData('confirmPassword', true),
					placeholder: 'Повторите пароль',
					isRequired: true,
					type: 'password',
				},
			],
		},
		3: {
			title: 'Данные организации',
			subtitle:
				'Заполните данные организации, от имени которой Вы будете работать',
			formFields: [
				{
					id: 'name',
					value: companyData.name,
					onChange: handleChangeCompanyData('name', true),
					placeholder: 'Наименование организации',
					isRequired: true,
				},
				{
					id: 'tin',
					value: companyData.tin,
					onChange: handleChangeCompanyData('tin', true),
					placeholder: 'ИНН',
					isRequired: true,
				},
				{
					id: 'kpp',
					value: companyData.kpp,
					onChange: handleChangeCompanyData(
						'kpp',
						!companyData.is_ie
					),
					placeholder: 'КПП',
					isRequired: true,
					type: 'number',
				},
				{
					id: 'ogrn',
					value: companyData.ogrn,
					onChange: handleChangeCompanyData('ogrn', true),
					placeholder: 'ОГРН',
					isRequired: true,
				},
				{
					id: 'country',
					value: companyData.country,
					onChange: handleChangeCompanyData('country', true),
					placeholder: 'Страна',
					isRequired: true,
				},
				{
					id: 'region',
					value: companyData.region,
					onChange: handleChangeCompanyData('region', true),
					placeholder: 'Регион',
					isRequired: true,
				},
				{
					id: 'address',
					value: companyData.address,
					onChange: handleChangeCompanyData('address', true),
					placeholder: 'Адрес регистрации',
					isRequired: true,
				},
				{
					id: 'phone',
					value: companyData.phone,
					onChange: handleChangeCompanyData('phone', true),
					placeholder: 'Номер телефона',
					isRequired: true,
					type: 'phone',
				},
			],
		},
	};
	const { title, subtitle, formFields } = formData[step];

	const getSubmitData = (): IValidateForm => {
		const submitData: IValidateForm = {
			formData: {},
			requiredFields: [],
			confirmValues: {},
		};
		switch (step) {
			case 1:
				submitData.formData = userData;
				submitData.requiredFields = ['lastName', 'firstName', 'email'];
				break;

			case 2:
				submitData.formData = passwordData;
				submitData.confirmValues = {
					confirmPassword: passwordData.password,
				};
				submitData.isRequired = true;
				break;

			case 3:
				submitData.formData = {
					name: companyData.name,
					tin: companyData.tin,
					kpp: companyData.kpp,
					ogrn: companyData.ogrn,
					country: companyData.country,
					region: companyData.region,
					address: companyData.address,
					phone: companyData.phone,
				};
				submitData.confirmValues = {
					ogrn: companyData.is_ie,
				};
				const requiredFields: string[] = [];
				Object.keys(submitData.formData).forEach((dataKey) => {
					if (dataKey !== 'kpp' || !companyData.is_ie) {
						requiredFields.push(dataKey);
					}
				});
				submitData.requiredFields = requiredFields;
				break;

			default:
				break;
		}
		return submitData;
	};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		if (isLoading) return;

		const submitData = getSubmitData();
		const { isValid, formErrors } = validateForm(submitData);
		if (!isValid) {
			return setErrors({ ...errors, ...formErrors });
		}

		if (step !== 3) {
			return setStep(step + 1);
		}

		setIsLoading(true);
		try {
			await registration({
				user: { ...userData, ...passwordData },
				company: companyData,
			});
			YM.onFormSubmit();
			setIsModalOpen(true);
		} catch (e) {
			const errorMessage =
				(e as AxiosError)?.response?.data?.message || '';
			setRegistrationError(errorMessage);
		}
		setIsLoading(false);
	};

	const handleCloseModal = (): void => {
		setIsModalOpen(false);
		navigate('../login');
	};

	const handleCloseErrorModal = (): void => {
		setRegistrationError('');
	};

	const handleGoBack = (): void => {
		if (step !== 1) {
			setStep(step - 1);
		}
	};

	const renderFormField = ({
		id,
		value,
		onChange,
		onBlur,
		placeholder,
		type = 'text',
	}: IFormField): JSX.Element => {
		const isTinIe = id === 'tin' && value.length === TIN_IE_LENGTH;
		const defaultProps = {
			className: classes.field,
			value,
			onChange,
			onBlur,
			placeholder,
			error: errors[id],
		};
		let field = null;
		switch (type) {
			case 'password':
				field = <InputPassword {...defaultProps} />;
				break;

			case 'phone':
				field = <InputPhone {...defaultProps} />;
				break;

			default:
				field = <Input {...defaultProps} type={type} />;
				break;
		}
		return (
			<React.Fragment key={id}>
				{field}
				{isTinIe && !errors.tin && (
					<div className={classes.bottomText}>
						Индивидуальный предприниматель
					</div>
				)}
			</React.Fragment>
		);
	};

	const formButtons = (
		<div className={classes.formButtons}>
			<Button
				className={classes.button}
				type="submit"
				text={step === 3 ? 'Завершить регистрацию' : 'Далее'}
				isLoading={isLoading}
			/>
			{step !== 1 && (
				<Button
					className={classes.button}
					text="Назад"
					type="button"
					onClick={handleGoBack}
				/>
			)}
			{step === 1 && (
				<div className={classes.privacyPolicyLink}>
					Продолжая регистрацию вы соглашаетесь с условиями{' '}
					<Link
						target="_blank"
						rel="noreferrer"
						className={classes.link}
						to="/terms"
					>
						Пользовательского соглашения
					</Link>
				</div>
			)}
			<div className={classes.loginText}>
				Уже есть аккаунт? Тогда выполните{' '}
				<Link className={classes.link} to="../login">
					вход
				</Link>
			</div>
		</div>
	);

	const form = (
		<form className={classes.form} onSubmit={handleSubmit}>
			<div className={classes.formInputs}>
				{formFields.map(renderFormField)}
			</div>
			{formButtons}
		</form>
	);

	return (
		<Card
			className={classes.registration}
			title={title}
			titleClassName={classes.title}
			subtitle={subtitle}
			subtitleClassName={classes.subtitle}
		>
			{form}
			<ModalInfo
				className={classes.modal}
				isOpen={isModalOpen}
				onClose={handleCloseModal}
				title="Регистрация завершена успешно!"
				text="Войдите в личный кабинет, чтобы начать использовать рекламные инструменты и другие решения для бизнеса"
				buttonText="Войти"
			/>
			<ModalError
				className={classes.modal}
				isOpen={!!registrationError}
				onClose={handleCloseErrorModal}
				text={registrationError}
			/>
		</Card>
	);
};

export default Registration;
